import React, { useState } from 'react';
import { useApiMutation } from "../../../hooks/useMutation";
import { toast } from "react-hot-toast";
import Input from '../../../utils/Input';
import Button from '../../../utils/Button';

interface BYOFormData {
  byoProvider: string;
  byoAccountSid: string;
  byoBrandName: string;
  byoAuthToken: string;
  byoPhoneNumber: string;
}

const BYO = ({ setShowBYOForm }: { setShowBYOForm: (show: boolean) => void }) => {
  const [formData, setFormData] = useState<BYOFormData>({
    byoProvider: "twilio",
    byoAccountSid: "",
    byoBrandName: "",
    byoAuthToken: "",
    byoPhoneNumber: "",
  });

  const submitMutation = useApiMutation({
    onSuccess: () => {
      toast.success("BYO Provider configured successfully!");
      // Reset form
      setFormData({
        byoProvider: "twilio",
        byoAccountSid: "",
        byoBrandName: "",
        byoAuthToken: "",
        byoPhoneNumber: "",
      });
    },
    onError: () => toast.error("Failed to configure BYO provider")
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.byoAccountSid || !formData.byoAuthToken || !formData.byoPhoneNumber) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Phone number validation
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(formData.byoPhoneNumber)) {
      toast.error("Please enter a valid phone number with country code");
      return;
    }

    submitMutation.mutate({
      method: "PUT",
      url: "/partner/messaging/byo",
      data: formData,
    });
  };

  const handleInputChange = (field: keyof BYOFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-6">
      <div className="max-w-4x w-full mx-auto">
        <div className="flex items-center justify-between">
          <div className="text-left mb-8">

            <h1 className="text-3xl font-bold text-gray-900 mb-2">Bring Your Own Provider</h1>
            <p className="text-gray-600">Connect your existing SMS provider to our platform</p>
          </div>

          <div>
            <Button
              onClick={() => setShowBYOForm(false)}
              variant="secondary"
              label="← Back to Providers"
              size="large"
            />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Provider Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                SMS Provider
              </label>
              <div className="grid grid-cols-1 gap-3">
                <div className="relative">
                  <Input
                    value={formData.byoProvider}
                    onChange={(e) => handleInputChange("byoProvider", e.target.value)}
                    placeholder="Select provider"
                    color="white"
                    size="large"
                    disabled
                    className="bg-gray-50"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-sm text-gray-500">Twilio</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Account SID */}
            <div className="space-y-2">
              <Input
                label="Account SID"
                value={formData.byoAccountSid}
                onChange={(e) => handleInputChange("byoAccountSid", e.target.value)}
                placeholder="Enter your Twilio Account SID"
                color="white"
                size="large"
                required
                helper="Found in your Twilio console dashboard"
              />
            </div>

            {/* Brand Name */}
            <div className="space-y-2">
              <Input
                label="Brand Name"
                value={formData.byoBrandName}
                onChange={(e) => handleInputChange("byoBrandName", e.target.value)}
                placeholder="Your brand name for SMS"
                color="white"
                size="large"
                required
                helper="This name will appear in SMS messages"
              />
            </div>

            {/* Auth Token */}
            <div className="space-y-2">
              <Input
                label="Auth Token"
                type="password"
                value={formData.byoAuthToken}
                onChange={(e) => handleInputChange("byoAuthToken", e.target.value)}
                placeholder="Enter your Twilio Auth Token"
                color="white"
                size="large"
                required
                helper="Keep this secure and never share it publicly"
              />
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Input
                label="Phone Number"
                type="tel"
                value={formData.byoPhoneNumber}
                onChange={(e) => handleInputChange("byoPhoneNumber", e.target.value)}
                placeholder="+12345678901"
                color="white"
                size="large"
                required
                helper="Include country code (e.g., +1 for US)"
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Setup Instructions</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>1. Log into your Twilio dashboard</p>
                    <p>2. Copy your Account SID and Auth Token</p>
                    <p>3. Ensure you have a verified phone number</p>
                    <p>4. Enter the details above and save</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-4 pt-4">
              <Button
                onClick={() => setShowBYOForm(false)}
                variant="secondary"
                label="← Back to Providers"
                size="large"
              />
              <Button
                type="submit"
                variant="primary"
                disabled={submitMutation.isPending}
                label={submitMutation.isPending ? "Connecting..." : "Connect Provider"}
                size="large"
              />
            </div>
          </form>
        </div>

        {/* Security Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Your credentials are encrypted and stored securely. We never share your API keys with third parties.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BYO;